import { Box, Button, Flex } from '@chakra-ui/react'
import {
  ClinicalRationaleMatch,
  clinicalRelationshipToPregnancyField,
  SCOPE_OF_PRACTICE_STATEMENT,
  ScopeOfPracticeConfig,
  SETTINGS,
} from '@hb/shared'
import { useCallback, useEffect, useRef } from 'react'
import { useForm } from 'react-final-form'
import { useDocument } from '../../../../backend'
import { ActionButton } from '../../../../components/Buttons'
import { FormElement } from '../../../../components/forms'
import { useAutofill } from '../AutofillContext'
import { SuggestedClinicalRationales } from './SuggestedClinicalRationales'
import { SectionLabel } from '../../SectionLabel'

const DEFAULT_SCOPE_OF_PRACTICE_TEXT =
  "This evaluation and management service was provided by a licensed midwife practicing within their authorized scope of practice. Midwifery scope of practice includes the independent management of women's health care, with a focus on pregnancy, childbirth, and the postpartum period, including the management of common health conditions that arise during pregnancy. This service is within the provider's licensure and clinical competency as defined by their credentialing body and applicable state law."

const ScopeOfPracticeLink = () => {
  const form = useForm()
  const { data: config } = useDocument<ScopeOfPracticeConfig>(SETTINGS, SCOPE_OF_PRACTICE_STATEMENT)

  const handleInsert = useCallback(() => {
    const text = config?.text ?? DEFAULT_SCOPE_OF_PRACTICE_TEXT
    const current =
      (form.getFieldState('data.clinicalRelationshipToPregnancy')?.value as string) ?? ''
    form.change(
      'data.clinicalRelationshipToPregnancy',
      current.trim() ? `${current.trim()}\n\n${text}` : text,
    )
  }, [form, config])

  return (
    <Button
      variant="link"
      size="sm"
      colorScheme="green"
      fontWeight={400}
      mt={1}
      mb={3}
      onClick={handleInsert}>
      + Add scope of practice statement
    </Button>
  )
}

export const NonRoutinePrenatalInput = () => {
  const form = useForm()
  const { clinicalRationaleSuggestions, rationaleLoading, fetchRationaleSuggestions } =
    useAutofill()
  const hasFired = useRef(false)

  // Fallback: if no suggestions were bundled with an autofill run (e.g. reopened draft, or advanced
  // without notes), fetch once on mount from the chief complaint.
  useEffect(() => {
    if (clinicalRationaleSuggestions !== null || hasFired.current) return
    const chiefComplaint = form.getFieldState('data.chiefComplaint')?.value as string
    if (!chiefComplaint?.trim()) return
    hasFired.current = true
    fetchRationaleSuggestions(chiefComplaint)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMatchClick = useCallback(
    (match: ClinicalRationaleMatch) => {
      form.change('data.clinicalRelationshipToPregnancy', match.suggestion)
    },
    [form],
  )

  const handleFetchFromNotes = useCallback(() => {
    const notes = (form.getFieldState('data.notes')?.value as string) ?? ''
    fetchRationaleSuggestions(notes)
  }, [form, fetchRationaleSuggestions])

  return (
    <Box data-child-name="data.clinicalRelationshipToPregnancy" px={5} py="14px" w="100%">
      <SectionLabel>Clinical Rationale</SectionLabel>
      <FormElement
        root
        // showName={false}
        field={clinicalRelationshipToPregnancyField}
        name="data.clinicalRelationshipToPregnancy"
        disabled={rationaleLoading}
      />
      <SuggestedClinicalRationales
        loading={rationaleLoading}
        matches={clinicalRationaleSuggestions ?? []}
        onMatchClick={handleMatchClick}
      />
      <Flex align="center" gap={2} flexWrap="wrap">
        <ActionButton size="xs" onClick={handleFetchFromNotes} isLoading={rationaleLoading}>
          Fetch suggestions from notes
        </ActionButton>
        <ScopeOfPracticeLink />
      </Flex>
    </Box>
  )
}
